<template>
  <header
    id="navbar"
    class="sticky top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-neutral-200"
  >
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- 品牌 -->
        <router-link to="/" class="flex items-center flex-shrink-0">
          <i class="fa fa-bolt text-primary text-2xl mr-2"></i>
          <span class="text-base lg:text-lg font-semibold whitespace-nowrap">
            <span class="hidden sm:inline">小义AI教育工具平台</span>
            <span class="sm:hidden">小义AI平台</span>
          </span>
        </router-link>

        <!-- 桌面端三大板块 -->
        <nav class="hidden lg:flex items-center space-x-1 flex-1 justify-center">
          <!-- 首页 -->
          <router-link
            to="/"
            class="px-3 py-2 text-sm rounded-lg hover:bg-neutral-50 apple-transition"
            :class="
              $route.path === '/' ? 'text-primary font-bold' : 'text-neutral-700'
            "
          >
            首页
          </router-link>

          <!-- 板块一：AI应用广场 -->
          <div
            class="relative"
            @mouseenter="openMenu = 'app'"
            @mouseleave="openMenu = ''"
          >
            <button
              class="flex items-center px-3 py-2 text-sm rounded-lg hover:bg-neutral-50 apple-transition"
              :class="
                isAppActive ? 'text-primary font-bold' : 'text-neutral-700'
              "
              @click="openMenu = openMenu === 'app' ? '' : 'app'"
            >
              AI应用广场
              <i class="fa fa-angle-down ml-1 text-xs opacity-60"></i>
            </button>
            <transition name="apple-fade">
              <div
                v-show="openMenu === 'app'"
                class="absolute left-0 top-full mt-1 w-[430px] bg-white rounded-xl shadow-xl border border-neutral-100 p-2 grid grid-cols-2 gap-1"
              >
                <router-link
                  to="/square"
                  class="col-span-2 flex items-center gap-2.5 p-2.5 rounded-lg bg-primary/5 hover:bg-primary/10 group"
                  @click="openMenu = ''"
                >
                  <span
                    class="w-8 h-8 rounded-lg bg-primary text-white flex items-center justify-center flex-shrink-0"
                  >
                    <i class="fa fa-th-large text-sm"></i>
                  </span>
                  <span class="min-w-0">
                    <span class="block text-sm font-medium text-neutral-800"
                      >应用广场</span
                    >
                    <span class="block text-xs text-neutral-400 truncate"
                      >工具 / 智能体 / 案例 / 网站 / 软件 / 提示词 / 需求</span
                    >
                  </span>
                </router-link>
                <div class="col-span-2 border-t border-neutral-100"></div>
                <router-link
                  v-for="m in appMenus"
                  :key="m.to"
                  :to="m.to"
                  class="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-neutral-50 group"
                  @click="openMenu = ''"
                >
                  <span
                    class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-colors"
                  >
                    <i :class="['fa', m.icon]" class="text-sm"></i>
                  </span>
                  <span class="min-w-0">
                    <span class="block text-sm font-medium text-neutral-800">{{
                      m.label
                    }}</span>
                    <span class="block text-xs text-neutral-400 truncate">{{
                      m.desc
                    }}</span>
                  </span>
                </router-link>
              </div>
            </transition>
          </div>

          <!-- 板块二：我的AI工作台 -->
          <button
            class="px-3 py-2 text-sm rounded-lg hover:bg-neutral-50 apple-transition"
            :class="
              $route.path === '/user'
                ? 'text-primary font-bold'
                : 'text-neutral-700'
            "
            @click="goWorkbench"
          >
            我的AI工作台
          </button>

          <!-- 板块三：AI创想广场 -->
          <div
            class="relative"
            @mouseenter="openMenu = 'create'"
            @mouseleave="openMenu = ''"
          >
            <button
              class="flex items-center px-3 py-2 text-sm rounded-lg hover:bg-neutral-50 apple-transition"
              :class="
                isCreateActive ? 'text-primary font-bold' : 'text-neutral-700'
              "
              @click="openMenu = openMenu === 'create' ? '' : 'create'"
            >
              AI创想广场
              <i class="fa fa-angle-down ml-1 text-xs opacity-60"></i>
            </button>
            <transition name="apple-fade">
              <div
                v-show="openMenu === 'create'"
                class="absolute left-0 top-full mt-1 w-[320px] bg-white rounded-xl shadow-xl border border-neutral-100 p-2"
              >
                <router-link
                  to="/create"
                  class="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-neutral-50 group"
                  @click="openMenu = ''"
                >
                  <span
                    class="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500 group-hover:text-white transition-colors"
                  >
                    <i class="fa fa-lightbulb-o text-sm"></i>
                  </span>
                  <span>
                    <span class="block text-sm font-medium text-neutral-800"
                      >创想广场</span
                    >
                    <span class="block text-xs text-neutral-400"
                      >看看大家的创作与需求</span
                    >
                  </span>
                </router-link>
                <div class="border-t border-neutral-100 my-1"></div>
                <router-link
                  v-for="m in createMenus"
                  :key="m.to"
                  :to="m.to"
                  class="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-neutral-50 text-sm text-neutral-700"
                  @click="openMenu = ''"
                >
                  <i :class="['fa', m.icon]" class="w-5 text-center text-neutral-400"></i>
                  {{ m.label }}
                </router-link>
              </div>
            </transition>
          </div>

          <!-- 软件下载（一级入口，与首页同级） -->
          <router-link
            to="/software"
            class="px-3 py-2 text-sm rounded-lg hover:bg-neutral-50 apple-transition"
            :class="
              $route.path === '/software'
                ? 'text-primary font-bold'
                : 'text-neutral-700'
            "
          >
            软件下载
          </router-link>
        </nav>

        <!-- 右侧工具区 -->
        <div class="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          <!-- 小义问宝 -->
          <button
            @click="openAskBot"
            class="hidden sm:flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-sky-400 to-primary text-white text-sm font-bold hover:opacity-90 apple-transition"
            title="小义问宝 · 站内智能助手"
          >
            义
          </button>

          <!-- 消息中心 -->
          <button
            v-if="isLogin"
            @click="goMessages"
            class="relative flex items-center justify-center w-9 h-9 rounded-lg hover:bg-neutral-100 apple-transition"
            title="消息中心"
          >
            <i class="fa fa-bell-o text-neutral-600"></i>
            <span
              v-if="unreadTotal > 0"
              class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center"
              >{{ unreadTotal > 99 ? "99+" : unreadTotal }}</span
            >
          </button>

          <!-- 创值 -->
          <div
            class="hidden xl:flex items-center text-sm text-neutral-500 whitespace-nowrap"
            v-if="isLogin"
          >
            <i class="fa fa-star text-yellow-400 mr-1"></i>
            <span id="user-points">{{ user.points }}</span>
            <span class="ml-1">创值</span>
          </div>

          <!-- 用户菜单 -->
          <div
            class="relative"
            id="user-menu-container"
            v-click-outside="closeDropdown"
          >
            <div
              v-if="!isLogin"
              id="not-logged-in"
              class="flex items-center space-x-3"
            >
              <router-link
                to="/login"
                class="text-sm whitespace-nowrap text-neutral-700 hover:text-primary apple-transition"
                >登录</router-link
              >
              <router-link
                to="/register"
                class="text-sm whitespace-nowrap px-3 py-1 bg-primary text-white rounded-full hover:bg-primary/90 apple-transition"
                >注册</router-link
              >
            </div>

            <div v-else class="relative">
              <button
                @click="toggleDropdown"
                class="flex items-center space-x-2 focus:outline-none"
              >
                <img
                  :src="user.avatar"
                  alt="User Avatar"
                  class="w-8 h-8 rounded-full border border-neutral-200 object-cover"
                />
                <span
                  class="text-sm font-medium text-neutral-700 hidden xl:block max-w-[110px] truncate"
                  >{{ user.name }}</span
                >
                <i
                  class="fa fa-chevron-down text-xs text-neutral-400 transition-transform duration-200"
                  :class="{ 'rotate-180': isDropdownOpen }"
                ></i>
              </button>

              <div
                v-show="isDropdownOpen"
                class="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-neutral-100 py-1 z-50 animate-fade-in-down origin-top-right"
              >
                <div class="px-4 py-3 border-b border-neutral-100">
                  <p class="text-sm font-medium text-neutral-900 truncate">
                    {{ user.name || user.username }}
                  </p>
                  <p class="text-xs text-neutral-400 mt-0.5">我的AI工作台</p>
                </div>
                <router-link
                  to="/user"
                  class="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary transition-colors"
                  @click="closeDropdown"
                >
                  <i class="fa fa-th-large w-5"></i> AI工作台
                </router-link>
                <router-link
                  to="/workbench"
                  class="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary transition-colors"
                  @click="closeDropdown"
                >
                  <i class="fa fa-window-maximize w-5"></i> HTML 工作台
                </router-link>
                <router-link
                  to="/create"
                  class="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary transition-colors"
                  @click="closeDropdown"
                >
                  <i class="fa fa-lightbulb-o w-5"></i> 创想广场
                </router-link>
                <router-link
                  to="/submit"
                  class="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary transition-colors"
                  @click="closeDropdown"
                >
                  <i class="fa fa-plus-circle w-5"></i> 发布工具
                </router-link>
                <router-link
                  v-if="isAdmin"
                  to="/admin"
                  class="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary transition-colors"
                  @click="closeDropdown"
                >
                  <i class="fa fa-shield w-5"></i> 管理后台
                </router-link>
                <div class="border-t border-neutral-100 my-1"></div>
                <button
                  @click="logout"
                  class="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                >
                  <i class="fa fa-sign-out w-5"></i> 退出登录
                </button>
              </div>
            </div>
          </div>

          <!-- 移动端菜单按钮 -->
          <button
            id="mobile-menu-toggle"
            class="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-neutral-700 hover:bg-neutral-100 focus:outline-none"
            :aria-expanded="mobileOpen ? 'true' : 'false'"
            aria-label="展开菜单"
            @click="mobileOpen = !mobileOpen"
          >
            <i :class="mobileOpen ? 'fa fa-times' : 'fa fa-bars'"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 移动端菜单 -->
    <div
      v-show="mobileOpen"
      class="lg:hidden fixed inset-0 top-16 bg-black/20 z-40"
      @click="mobileOpen = false"
    ></div>
    <div
      v-show="mobileOpen"
      class="lg:hidden absolute left-0 right-0 top-16 z-50 bg-white border-t border-neutral-200 shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto"
    >
      <nav class="px-3 py-2">
        <!-- 首页 -->
        <router-link
          to="/"
          class="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
          active-class="text-primary font-bold bg-neutral-50"
          @click="mobileOpen = false"
        >
          <i class="fa fa-home w-5 text-center text-neutral-400"></i>
          首页
        </router-link>

        <!-- 板块一 -->
        <div class="border-t border-neutral-100 my-2"></div>
        <p class="px-3 pt-2 pb-1 text-xs font-bold text-neutral-400 tracking-wide">
          AI应用广场
        </p>
        <router-link
          to="/square"
          class="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
          active-class="text-primary font-bold bg-neutral-50"
          @click="mobileOpen = false"
        >
          <i class="fa fa-th-large w-5 text-center text-neutral-400"></i>
          应用广场
        </router-link>
        <router-link
          v-for="m in appMenus"
          :key="m.to"
          :to="m.to"
          class="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
          active-class="text-primary font-bold bg-neutral-50"
          @click="mobileOpen = false"
        >
          <i :class="['fa', m.icon]" class="w-5 text-center text-neutral-400"></i>
          {{ m.label }}
        </router-link>

        <!-- 板块二 -->
        <div class="border-t border-neutral-100 my-2"></div>
        <button
          class="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
          @click="mobileOpen = false; goWorkbench()"
        >
          <i class="fa fa-th-large w-5 text-center text-neutral-400"></i>
          我的AI工作台
        </button>
        <router-link
          to="/workbench"
          class="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
          active-class="text-primary font-bold bg-neutral-50"
          @click="mobileOpen = false"
        >
          <i
            class="fa fa-window-maximize w-5 text-center text-neutral-400"
          ></i>
          HTML 工作台
        </router-link>

        <!-- 板块三 -->
        <div class="border-t border-neutral-100 my-2"></div>
        <p class="px-3 pt-1 pb-1 text-xs font-bold text-neutral-400 tracking-wide">
          AI创想广场
        </p>
        <router-link
          to="/create"
          class="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
          active-class="text-primary font-bold bg-neutral-50"
          @click="mobileOpen = false"
        >
          <i class="fa fa-lightbulb-o w-5 text-center text-neutral-400"></i>
          创想广场
        </router-link>
        <router-link
          v-for="m in createMenus"
          :key="m.to"
          :to="m.to"
          class="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
          active-class="text-primary font-bold bg-neutral-50"
          @click="mobileOpen = false"
        >
          <i :class="['fa', m.icon]" class="w-5 text-center text-neutral-400"></i>
          {{ m.label }}
        </router-link>

        <!-- 软件下载（一级入口） -->
        <div class="border-t border-neutral-100 my-2"></div>
        <router-link
          to="/software"
          class="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-neutral-700 rounded-lg hover:bg-neutral-50"
          active-class="text-primary font-bold bg-neutral-50"
          @click="mobileOpen = false"
        >
          <i class="fa fa-download w-5 text-center text-neutral-400"></i>
          软件下载
        </router-link>

        <!-- 其他 -->
        <div class="border-t border-neutral-100 my-2"></div>
        <button
          class="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-primary rounded-lg hover:bg-neutral-50"
          @click="mobileOpen = false; openAskBot()"
        >
          <span
            class="w-5 h-5 rounded-md bg-gradient-to-br from-sky-400 to-primary text-white text-[11px] flex items-center justify-center font-bold"
            >义</span
          >
          小义问宝
        </button>
      </nav>

      <div class="border-t border-neutral-100 px-3 py-2">
        <template v-if="isLogin">
          <router-link
            to="/messages"
            class="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
            @click="mobileOpen = false"
          >
            <i class="fa fa-bell-o w-5 text-center"></i> 消息中心
            <span v-if="unreadTotal > 0" class="ml-1 text-xs text-red-500">{{
              unreadTotal > 99 ? "99+" : unreadTotal
            }}</span>
          </router-link>
          <button
            class="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-red-500 rounded-lg hover:bg-red-50"
            @click="mobileOpen = false; logout()"
          >
            <i class="fa fa-sign-out w-5 text-center"></i> 退出登录
          </button>
        </template>
        <template v-else>
          <router-link
            to="/login"
            class="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 rounded-lg hover:bg-neutral-50"
            @click="mobileOpen = false"
          >
            <i class="fa fa-sign-in w-5 text-center"></i> 登录
          </router-link>
          <router-link
            to="/register"
            class="flex items-center gap-3 px-3 py-2.5 text-sm text-primary font-medium rounded-lg hover:bg-neutral-50"
            @click="mobileOpen = false"
          >
            <i class="fa fa-user-plus w-5 text-center"></i> 注册
          </router-link>
        </template>
      </div>
    </div>
  </header>
</template>

<script>
import { ElMessageBox } from "element-plus";
import { globalState } from "../state";
import { loadDownloadFree } from "../utils/downloadRule";

export default {
  name: "nav-bar",
  data() {
    return {
      isDropdownOpen: false,
      openMenu: "",
      points: 0,
      unreadTotal: 0,
      unreadTimer: null,
      mobileOpen: false,
      /** 是否有后台权限（决定是否显示「管理后台」入口） */
      isAdmin: false,
      /** 板块一：AI应用广场 */
      appMenus: [
        { to: "/explore", label: "AI工具", icon: "fa-cube", desc: "开箱即用的教学工具" },
        { to: "/agents", label: "AI智能体", icon: "fa-robot", desc: "对话式教学助手" },
        { to: "/requests", label: "新需求", icon: "fa-lightbulb-o", desc: "大家都在提什么" },
        { to: "/cases", label: "应用案例", icon: "fa-graduation-cap", desc: "真实课堂落地案例" },
        { to: "/webinfo", label: "网站集合", icon: "fa-window-maximize", desc: "精选教育站点" },
        { to: "/prompts", label: "提示词技巧", icon: "fa-bookmark", desc: "好用的提示词模板" },
        { to: "/rankings", label: "排行榜", icon: "fa-trophy", desc: "热门工具与创作者" },
      ],
      /** 板块三：AI创想广场（教师创作） */
      createMenus: [
        { to: "/submit", label: "发布工具", icon: "fa-plus-circle" },
        { to: "/request", label: "发布需求", icon: "fa-question-circle-o" },
        { to: "/cases/submit", label: "投稿案例", icon: "fa-file-text-o" },
        { to: "/prompts", label: "分享提示词", icon: "fa-bookmark-o" },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isLogin() {
      return this.user && this.user.uid;
    },
    isAppActive() {
      const p = this.$route.path;
      // 应用广场聚合页高亮
      if (p === "/square") return true;
      return this.appMenus.some((m) => p === m.to || p.startsWith(m.to + "/"));
    },
    isCreateActive() {
      const p = this.$route.path;
      return (
        p === "/create" ||
        p === "/submit" ||
        p === "/request" ||
        p === "/cases/submit"
      );
    },
  },
  watch: {
    $route() {
      this.mobileOpen = false;
      this.openMenu = "";
    },
    isLogin(newVal) {
      if (newVal) {
        this.fetchUserPoints();
      }
      this.fetchAdminPerms();
      loadDownloadFree(this.$axios, true);
      this.fetchUnread();
    },
  },
  mounted() {
    if (this.isLogin) {
      this.fetchUserPoints();
    }
    this.fetchAdminPerms();
    loadDownloadFree(this.$axios, true);
    this.fetchUnread();
    this.unreadTimer = setInterval(() => this.fetchUnread(), 60000);
  },
  beforeUnmount() {
    if (this.unreadTimer) clearInterval(this.unreadTimer);
  },
  methods: {
    openAskBot() {
      globalState.openAskBot("");
    },
    /** 进入我的AI工作台（需登录） */
    goWorkbench() {
      if (!this.isLogin) {
        ElMessageBox.confirm("登录后即可拥有自己的 AI 工作台", "登录", {
          confirmButtonText: "去登录",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => this.$router.push("/login"))
          .catch(() => {});
        return;
      }
      this.$router.push("/user");
    },
    checkLogin(router) {
      if (!this.isLogin) {
        ElMessageBox.confirm("需要使用当前功能，请先登录账号", "登录", {
          confirmButtonText: "登录",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            this.$router.push("/login");
          })
          .catch(() => {});
        return;
      }

      this.$axios({
        url: "aitool/user/checklogin",
      }).then((res) => {
        if (res.code == 200) {
          this.$router.push(router);
        }
      });
    },
    fetchUnread() {
      if (!this.isLogin) {
        this.unreadTotal = 0;
        return;
      }
      this.$axios({ url: "notice/unread", method: "GET" })
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.unreadTotal = res.data.total || 0;
          }
        })
        .catch(() => {});
    },
    goMessages() {
      this.$router.push("/messages");
    },
    /** 查询后台权限，只有管理员才显示「管理后台」入口 */
    fetchAdminPerms() {
      if (!this.isLogin) {
        this.isAdmin = false;
        return;
      }
      this.$axios({ url: "user/admin/perms", method: "POST" })
        .then((res) => {
          if (res.code === 200 && res.data) {
            const perms = res.data.perms || "";
            this.isAdmin =
              res.data.utype === "admin" ||
              (typeof perms === "string" && perms.length > 0);
          }
        })
        .catch(() => {
          this.isAdmin = false;
        });
    },
    fetchUserPoints() {
      this.$axios({
        url: "user/integral",
      }).then((res) => {
        if (res.code == 200) {
          var points = res.data.point;
          this.$store.commit("setPoints", points);
        }
      });
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    logout() {
      this.closeDropdown();
      if (confirm("确定要退出登录吗？")) {
        localStorage.clear();
        this.$store.commit("clearUserInfo");
        this.$router.push("/");
      }
    },
  },
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event, el);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
};
</script>
